import { useState, useEffect, useRef } from 'react';
import { useI18n } from '../context/I18nContext';
import { useApp } from '../context/AppContext';
import SpinningLogo from './SpinningLogo';
import { useVoiceCommand } from '../context/VoiceCommandContext';
import { API_BASE } from '../config';

export default function LoginScreen({ onLogin, onShowRegister }) {
  const { t, lang, switchLang } = useI18n();
  const { setShowHelp, setShowCharter } = useApp();
  const { registerField, unregisterField } = useVoiceCommand();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const headingRef = useRef(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    registerField('email', (val) => {
      // Only apply voice command transforms, don't override manual typing
      if (typeof val === 'string' && val.length > 0) {
        setEmail(val.replace(/\s+/g, '').replace(/\bat\b/gi, '@').replace(/\bdot\b/gi, '.'));
      }
    });
    registerField('password', (val) => {
      if (typeof val === 'string') setPassword(val);
    });
    registerField('otp', (val) => {
      if (typeof val === 'string') setOtp(val.replace(/\D/g, '').slice(0, 6));
    });
    return () => {
      unregisterField('email');
      unregisterField('password');
      unregisterField('otp');
    };
  }, [registerField, unregisterField]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password: password.trim() })
      });
      const data = await res.json();
      if (data.success && data.requires2FA && data.tempToken) {
        setRequires2FA(true);
        setTempToken(data.tempToken);
      } else if (data.success && data.token) {
        localStorage.setItem('cogency_token', data.token);
        onLogin(data.token);
      } else {
        setError(data.message || data.error || t('loginFailed'));
      }
    } catch (err) {
      setError(t('networkErrorPleaseTryAgain'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!otp.trim() || !tempToken) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/2fa/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken, token: otp.trim() })
      });
      const data = await res.json();
      if (data.success && data.token) {
        localStorage.setItem('cogency_token', data.token);
        onLogin(data.token);
      } else {
        setError(data.error || data.message || t('loginFailed'));
      }
    } catch (err) {
      setError(t('networkErrorPleaseTryAgain'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-screen auth-screen" role="main" aria-label={t('login')}>
      <div className="setup-card auth-card">
        <div className="login-top-bar">
          <div className="setup-header-brand">
            <SpinningLogo size={110} className="brand-logo" />
            <div className="brand-text">
              <div className="brand-title">Cogency V1.0</div>
              <div className="brand-tagline">{t('brandTagline')}</div>
            </div>
          </div>
          <select
            className="lang-select"
            value={lang}
            onChange={e => switchLang(e.target.value)}
            aria-label={t('language')}
          >
            <option value="en">English</option>
            <option value="fr">Français</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
            <option value="ar">العربية</option>
            <option value="zh">中文</option>
          </select>
        </div>

        <div className="login-hero">
          <img src="/brain-vs-ai.png" alt="Cogency" className="login-hero-img" />
        </div>

        <form onSubmit={requires2FA ? handleVerify2FA : handleSubmit} className="login-form" noValidate>
          {!requires2FA ? (
            <>
              <div className="field-group">
                <input
                  id="login-email"
                  type="email"
                  placeholder={t('email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoFocus
                  aria-required="true"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>

              <div className="field-group">
                <input
                  id="login-password"
                  type="password"
                  placeholder={t('password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  aria-required="true"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
            </>
          ) : (
            <>
              <div className="field-group">
                <input
                  id="login-otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder={t('verificationCode') || 'Code à 6 chiffres'}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  autoFocus
                  aria-required="true"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
            </>
          )}

          {error && (
            <div id="login-error" className="setup-error" role="alert" aria-live="assertive">
              <p>{error}</p>
            </div>
          )}

          <button
            type="submit"
            className="btn-primary btn-large"
            disabled={loading || (requires2FA ? otp.length !== 6 : !email.trim() || !password.trim())}
            aria-busy={loading}
          >
            {loading ? t('authenticating') : requires2FA ? (t('verify') || 'Vérifier') : t('access')}
          </button>

          {requires2FA && (
            <button
              type="button"
              className="btn-ghost btn-small"
              onClick={() => { setRequires2FA(false); setTempToken(''); setOtp(''); setError(''); }}
              disabled={loading}
              style={{ marginTop: '10px' }}
            >
              {t('back') || 'Retour'}
            </button>
          )}
        </form>

        <div className="login-actions-row">
          <button className="btn-ghost" onClick={onShowRegister}>
            📝 {t('register')}
          </button>
          <button className="btn-ghost" onClick={() => setShowHelp(true)}>
            ❓ {t('howItWorks')}
          </button>
          <button className="btn-ghost" onClick={() => setShowCharter(true)}>
            📜 {t('viewCharter')}
          </button>
        </div>
      </div>
    </div>
  );
}
